import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Calendar,
  Clock,
  MapPin,
  BookOpen,
  AlertCircle,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  History,
  CalendarCheck,
  Filter,
  ChevronDown,
} from 'lucide-react';
import { fetchMyRattrapageSchedule, fetchMyRattrapageHistory } from '../services/studentService';
import { fetchChildRattrapageSchedule, fetchChildRattrapageHistory } from '@/features/parent/services/parentService';
import {
  StudentRattrapageScheduleResponse,
  StudentRattrapageEntry,
  StudentRattrapageHistoryResponse,
  getRattrapageStatusLabel,
  getRattrapageStatusColor,
  getRattrapageReasonLabel,
} from '@/types/student.types';

type TabType = 'schedule' | 'history';

// Helper to format date in French
const formatDateFr = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Helper to format short date
const formatDateShort = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// Helper to format time range
const formatTimeRange = (startTime: string, endTime: string): string => {
  return `${startTime} - ${endTime}`;
};

// Helper to format duration
const formatDuration = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, '0')}`;
};

// Helper to format grade
const formatGrade = (grade: number | null): string => {
  if (grade === null) return '-';
  return grade.toFixed(2);
};

// Group rattrapages by date
const groupRattrapagesByDate = (rattrapages: StudentRattrapageEntry[]): Map<string, StudentRattrapageEntry[]> => {
  const grouped = new Map<string, StudentRattrapageEntry[]>();
  rattrapages.forEach((r) => {
    const existing = grouped.get(r.date) || [];
    existing.push(r);
    grouped.set(r.date, existing);
  });
  return grouped;
};

interface RattrapageSchedulePageProps {
  /** When set (parent panel), loads this child's rattrapages instead of the logged-in student's */
  studentId?: string;
}

const RattrapageSchedulePage: React.FC<RattrapageSchedulePageProps> = ({ studentId }) => {
  const [activeTab, setActiveTab] = useState<TabType>('schedule');
  const [scheduleData, setScheduleData] = useState<StudentRattrapageScheduleResponse | null>(null);
  const [historyData, setHistoryData] = useState<StudentRattrapageHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Default to the most recent semester; users can switch back to S1
  const [selectedSemester, setSelectedSemester] = useState<number>(2);
  const [selectedYearFilter, setSelectedYearFilter] = useState<string>('');

  // Load schedule data
  const loadSchedule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = studentId
        ? await fetchChildRattrapageSchedule(studentId, undefined, selectedSemester)
        : await fetchMyRattrapageSchedule(undefined, selectedSemester);
      setScheduleData(response);
      if (response.semester) {
        setSelectedSemester(response.semester);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  }, [selectedSemester, studentId]);

  // Load history data
  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = studentId
        ? await fetchChildRattrapageHistory(studentId, selectedYearFilter || undefined)
        : await fetchMyRattrapageHistory(selectedYearFilter || undefined);
      setHistoryData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  }, [selectedYearFilter, studentId]);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'schedule') {
      loadSchedule();
    } else {
      loadHistory();
    }
  }, [activeTab, loadSchedule, loadHistory]);

  // Grouped rattrapages by date
  const groupedRattrapages = scheduleData?.rattrapages
    ? groupRattrapagesByDate(scheduleData.rattrapages)
    : new Map<string, StudentRattrapageEntry[]>();

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-amber-100 rounded-lg">
            <RefreshCw className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Rattrapages</h1>
            {scheduleData?.academicYear && activeTab === 'schedule' && (
              <p className="text-sm text-gray-500">Annee academique {scheduleData.academicYear}</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('schedule')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'schedule'
              ? 'bg-white text-amber-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <CalendarCheck className="w-4 h-4" />
          Mon Planning
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'history'
              ? 'bg-white text-amber-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <History className="w-4 h-4" />
          Historique
        </button>
      </div>

      {/* Schedule Tab Content */}
      {activeTab === 'schedule' && (
        <>
          {/* Semester tabs — only when the tenant runs one rattrapage session
              per semester; a SINGLE yearly session shows everything at once */}
          {scheduleData?.periodType === 'PER_SEMESTER' ? (
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setSelectedSemester(1)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedSemester === 1
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Semestre 1
              </button>
              <button
                onClick={() => setSelectedSemester(2)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedSemester === 2
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Semestre 2
              </button>
            </div>
          ) : (
            <div className="mb-6">
              <span className="inline-flex items-center px-3 py-1.5 bg-amber-50 text-amber-800 text-sm font-medium rounded-lg">
                Session de rattrapage — {scheduleData?.academicYear || 'année en cours'}
              </span>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
              <span className="ml-3 text-gray-600">Chargement...</span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Erreur</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Schedule Content */}
          {!loading && !error && scheduleData && (
            <>
              {/* Rattrapage Period Banner */}
              {scheduleData.rattrapagePeriod?.start && scheduleData.rattrapagePeriod?.end && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Calendar className="w-5 h-5" />
                    <span className="font-medium">Periode des rattrapages</span>
                  </div>
                  <p className="text-sm text-amber-700 mt-1">
                    Du {formatDateFr(scheduleData.rattrapagePeriod.start)} au{' '}
                    {formatDateFr(scheduleData.rattrapagePeriod.end)}
                  </p>
                </div>
              )}

              {/* All Passed - Success State */}
              {scheduleData.allPassed && !scheduleData.hasRattrapages && (
                <div className="bg-green-50 rounded-xl p-8 text-center border border-green-200">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-green-900 mb-2">
                    Aucun rattrapage planifie pour vous
                  </h3>
                  <p className="text-sm text-green-700">
                    Felicitations ! Vous avez valide toutes vos matieres.
                  </p>
                </div>
              )}

              {/* Pending Subjects - Waiting for Scheduling */}
              {!scheduleData.hasRattrapages &&
                scheduleData.pendingSubjects &&
                scheduleData.pendingSubjects.length > 0 && (
                  <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-amber-900">
                          Rattrapages en attente de planification
                        </h3>
                        <p className="text-sm text-amber-700">
                          {scheduleData.message || 'Le planning sera bientot disponible.'}
                        </p>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-amber-50">
                          <tr>
                            <th className="text-left px-4 py-2 text-sm font-medium text-amber-800">
                              Matiere
                            </th>
                            <th className="text-left px-4 py-2 text-sm font-medium text-amber-800">
                              Note originale
                            </th>
                            <th className="text-left px-4 py-2 text-sm font-medium text-amber-800">
                              Raison
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-100">
                          {scheduleData.pendingSubjects.map((subject, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-3">
                                <p className="font-medium text-gray-900">{subject.subject}</p>
                                {subject.moduleName && subject.moduleName !== subject.subject && (
                                  <p className="text-sm text-gray-500">{subject.moduleName}</p>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {formatGrade(subject.originalGrade)}/20
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {getRattrapageReasonLabel(subject.reason)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* Scheduled Rattrapages */}
              {scheduleData.hasRattrapages && scheduleData.rattrapages.length > 0 && (
                <div className="space-y-6">
                  {Array.from(groupedRattrapages.entries()).map(([date, rattrapages]) => (
                    <div
                      key={date}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                    >
                      {/* Date Header */}
                      <div className="bg-amber-50 px-4 py-3 border-b border-amber-100">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-amber-600" />
                          <span className="font-semibold text-gray-900 capitalize">
                            {formatDateFr(date)}
                          </span>
                        </div>
                      </div>

                      {/* Rattrapages for this date */}
                      <div className="divide-y divide-gray-100">
                        {rattrapages.map((r) => (
                          <div key={r.sessionId} className="p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start gap-4">
                              {/* Time Column */}
                              <div className="flex-shrink-0 w-24">
                                <div className="flex items-center gap-1.5 text-amber-600 font-medium">
                                  <Clock className="w-4 h-4" />
                                  <span>{r.startTime}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5 ml-5.5">
                                  {formatDuration(r.durationHours)}
                                </p>
                              </div>

                              {/* Details */}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900 truncate">{r.subject}</h4>
                                {r.moduleName && r.moduleName !== r.subject && (
                                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                                    <BookOpen className="w-3.5 h-3.5" />
                                    {r.moduleName}
                                  </p>
                                )}
                              </div>

                              {/* Room */}
                              <div className="flex-shrink-0 text-right">
                                <div className="flex items-center gap-1.5 text-gray-600">
                                  <MapPin className="w-4 h-4" />
                                  <span className="font-medium">{r.room}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {formatTimeRange(r.startTime, r.endTime)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Summary */}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <span className="font-medium">{scheduleData.rattrapages.length}</span>{' '}
                      rattrapage(s) programme(s){scheduleData.periodType === 'PER_SEMESTER' ? ` pour le semestre ${scheduleData.semester}` : ''}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* History Tab Content */}
      {activeTab === 'history' && (
        <>
          {/* Year Filter */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Filtrer par annee:</span>
            </div>
            <div className="relative">
              <select
                value={selectedYearFilter}
                onChange={(e) => setSelectedYearFilter(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="">Toutes les annees</option>
                {historyData?.academicYears.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
              <span className="ml-3 text-gray-600">Chargement de l'historique...</span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Erreur</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* History Content */}
          {!loading && !error && historyData && (
            <>
              {historyData.history.length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <History className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun historique</h3>
                  <p className="text-sm text-gray-500">
                    Vous n'avez pas encore d'historique de rattrapages.
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">
                            Matiere
                          </th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">
                            Annee
                          </th>
                          <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">
                            S.
                          </th>
                          <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">
                            Note orig.
                          </th>
                          <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">
                            Note rattr.
                          </th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">
                            Date
                          </th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">
                            Statut
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {historyData.history.map((entry) => (
                          <tr key={entry.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900">{entry.subject}</p>
                              {entry.moduleName && entry.moduleName !== entry.subject && (
                                <p className="text-xs text-gray-500">{entry.moduleName}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{entry.academicYear}</td>
                            <td className="px-4 py-3 text-center text-sm text-gray-600">
                              {entry.semester || '-'}
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-gray-600">
                              {formatGrade(entry.originalGrade)}/20
                            </td>
                            <td className="px-4 py-3 text-center text-sm font-medium">
                              <span
                                className={
                                  entry.rattrapageGrade !== null && entry.rattrapageGrade >= 10
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }
                              >
                                {formatGrade(entry.rattrapageGrade)}/20
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {formatDateShort(entry.examDate)}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRattrapageStatusColor(
                                  entry.status
                                )}`}
                              >
                                {getRattrapageStatusLabel(entry.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Summary */}
              {historyData.history.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{historyData.history.length}</span> rattrapage(s)
                    dans l'historique
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RattrapageSchedulePage;
